// segment.ts (S26) - turn a flat HandRecord into a per-street move-list for the replay timeline.
//
// The problem: a stored HandRecord (S16 import / S14 persist) keeps `spec`, `actions`, `board`,
// `holeCards`, `result` - but NOT the button seat or per-seat stacks. To render a Lichess-style
// move-list (reference lane #3) we must split the flat `actions` array into betting streets and know
// each actor's position. This module recovers both from the engine's OWN math, so the labels are
// truthful, not guessed:
//
//   1) Button recovery - the first recorded action is always the first voluntary actor (blinds are
//      posted in init, never recorded as actions). `preflopOrder(n, button)[0]` is a bijection
//      button -> first-actor, so we invert it: find the unique button whose preflop order starts at
//      actions[0].seat. From the button, `assignPositions` gives every seat its position.
//   2) Street segmentation - replay the action stream with a minimal betting model (no stacks needed):
//      track each seat's committed-this-street, the current bet, and the last aggressor; a street
//      closes when every still-in seat has matched the current bet AND acted since the last raise.
//      This is exact for hands without an all-in-for-less; the board length hard-caps the street count
//      so a malformed stream can never invent a street the board doesn't support.
//
// Pots shown are pot-at-street-start (cumulative committed through prior streets, seeded with the
// blinds). Mid-street running pots and uncalled-bet returns are a result detail, not a scrub label.
import {
  assignPositions,
  preflopOrder,
  BOARD_LENGTH_BY_STREET,
} from '@postflop/engine';
import type { Action, CardIndex, HandRecord, HandState, Position, Street } from '@postflop/schema';

const STREETS: Street[] = ['preflop', 'flop', 'turn', 'river'];
const STREET_LABEL: Record<Street, string> = {
  preflop: 'Preflop',
  flop: 'Flop',
  turn: 'Turn',
  river: 'River',
};

/** One rendered move in the list. `amount` is the increment a call puts in / the TO amount a
 *  bet/raise sets to, matching how hand histories read ("calls $4", "raises to $6", "bets $8"). */
export interface ReplayMove {
  seat: number;
  /** 'Hero' for the hero seat, else the position abbreviation (UTG, HJ, CO, BTN, SB, BB). */
  who: string;
  isHero: boolean;
  kind: Action['kind'];
  /** display verb: "folds" | "checks" | "calls" | "bets" | "raises to" | "jams". */
  verb: string;
  amount?: number;
  /** this action set a new bet (bet / raise / jam) - highlighted mint. */
  aggressor: boolean;
}

export interface ReplayStreet {
  street: Street;
  label: string;
  /** cumulative board visible on this street. */
  board: CardIndex[];
  /** pot at the start of this street (before its betting), whole chips. */
  potAtStart: number;
  moves: ReplayMove[];
}

export interface ReplaySegments {
  streets: ReplayStreet[];
  heroSeat: number;
  heroPosition: Position;
  heroCards: readonly [CardIndex, CardIndex];
  finalBoard: CardIndex[];
  /** "$1/$2" style. */
  stakesLabel: string;
  tableSize: number;
  buttonSeat: number;
  result: {
    winnerSeats: number[];
    potWon: number;
    heroWon: boolean;
    showdown: boolean;
  };
}

const VERB: Record<Action['kind'], string> = {
  fold: 'folds',
  check: 'checks',
  call: 'calls',
  bet: 'bets',
  raise: 'raises to',
  jam: 'jams',
};

/** Recover the button seat by inverting the engine's preflop order against the first recorded action.
 *  Falls back to the seat before the hero (a harmless default) if no action opens the hand. */
export function recoverButtonSeat(n: number, actions: readonly Action[], heroSeat: number): number {
  const first = actions[0]?.seat;
  if (first === undefined) return (heroSeat - 1 + n) % n;
  for (let b = 0; b < n; b++) {
    if (preflopOrder(n, b)[0] === first) return b;
  }
  return (heroSeat - 1 + n) % n;
}

/** Whole-chip pot rounding (dollars can carry cents through blind/call arithmetic). */
const round = (n: number): number => Math.round(n);

export function segmentHand(record: HandRecord): ReplaySegments {
  const n = record.spec.tableSize;
  const heroSeat = record.spec.heroSeat;
  const { sb, bb, ante = 0 } = record.spec.stakes;
  const buttonSeat = recoverButtonSeat(n, record.actions, heroSeat);
  const positions = assignPositions(n, buttonSeat);
  const posOf = (seat: number): Position => positions.get(seat) ?? 'BTN';

  const boardLen = record.board.length;
  // Streets the board actually supports (preflop always; flop needs 3 cards, etc.).
  const maxStreetIdx = STREETS.reduce(
    (max, s, i) => (BOARD_LENGTH_BY_STREET[s] <= boardLen ? i : max),
    0,
  );

  // Blinds are dead money already in the pot; the SB/BB seats' committed-this-street start there so a
  // later call/check reads as "matched" without double-counting.
  const sbSeat = [...positions].find(([, p]) => p === 'SB')?.[0];
  const bbSeat = [...positions].find(([, p]) => p === 'BB')?.[0];

  const streets: ReplayStreet[] = [];
  let potCarried = round(sb + bb + ante * n); // running pot (seeded with the blinds/antes)
  let streetStartPot = potCarried; // pot shown at the top of the current street (locked when it opens)
  let streetIdx = 0;

  // Per-street mutable betting state, (re)initialized at each street start.
  const startStreet = (idx: number): { committed: Map<number, number>; currentBet: number } => {
    const committed = new Map<number, number>();
    let currentBet = 0;
    if (idx === 0) {
      if (sbSeat !== undefined) committed.set(sbSeat, sb);
      if (bbSeat !== undefined) committed.set(bbSeat, bb);
      currentBet = bb;
    }
    return { committed, currentBet };
  };

  // Everyone dealt in is a contender; folds remove them.
  const inSeats = new Set<number>();
  for (let s = 0; s < n; s++) inSeats.add(s);

  let cur = startStreet(0);
  const actedSinceAggro = new Set<number>();
  let curMoves: ReplayMove[] = [];
  const boardFor = (idx: number): CardIndex[] =>
    record.board.slice(0, BOARD_LENGTH_BY_STREET[STREETS[idx]]);

  const pushStreet = (idx: number, moves: ReplayMove[], potAtStart: number) => {
    streets.push({
      street: STREETS[idx],
      label: STREET_LABEL[STREETS[idx]],
      board: boardFor(idx),
      potAtStart,
      moves,
    });
  };

  const closeAndAdvance = () => {
    // carry the pot forward: add everyone's committed this street, minus the blinds already seeded.
    let streetPot = 0;
    for (const v of cur.committed.values()) streetPot += v;
    potCarried = round(potCarried + streetPot - streetStartBlinds(streetIdx, sb, bb));
    pushStreet(streetIdx, curMoves, streetStartPot);
    streetIdx += 1;
    if (streetIdx > maxStreetIdx) return;
    cur = startStreet(streetIdx);
    actedSinceAggro.clear();
    curMoves = [];
    streetStartPot = potCarried;
  };

  const streetClosed = (): boolean => {
    const contenders = [...inSeats];
    if (contenders.length <= 1) return true; // everyone folded out; hand is decided
    for (const s of contenders) {
      if ((cur.committed.get(s) ?? 0) !== cur.currentBet) return false;
      if (!actedSinceAggro.has(s)) return false;
    }
    return true;
  };

  for (const a of record.actions) {
    if (streetIdx > maxStreetIdx) break; // board can't support more streets; stop bucketing
    const isHero = a.seat === heroSeat;
    const pos = posOf(a.seat);
    const move: ReplayMove = {
      seat: a.seat,
      who: isHero ? 'Hero' : pos,
      isHero,
      kind: a.kind,
      verb: VERB[a.kind],
      aggressor: false,
    };

    if (a.kind === 'fold') {
      inSeats.delete(a.seat);
    } else if (a.kind === 'check') {
      cur.committed.set(a.seat, cur.currentBet); // a check means already matched (0 or the BB option)
    } else if (a.kind === 'call') {
      const already = cur.committed.get(a.seat) ?? 0;
      move.amount = round(cur.currentBet - already); // the chips this call adds now
      cur.committed.set(a.seat, cur.currentBet);
    } else {
      // bet | raise | jam - `amount` is the TO amount; when absent (rare jam), treat as current bet.
      const to = a.amount ?? cur.currentBet;
      move.amount = round(to);
      move.aggressor = true;
      cur.committed.set(a.seat, to);
      cur.currentBet = Math.max(cur.currentBet, to);
      actedSinceAggro.clear();
    }
    actedSinceAggro.add(a.seat);
    curMoves.push(move);

    if (streetClosed()) closeAndAdvance();
  }

  // flush any trailing street that didn't formally close (hand ended mid-street, e.g. a fold-out).
  if (streetIdx <= maxStreetIdx && curMoves.length) {
    pushStreet(streetIdx, curMoves, streetStartPot);
  }

  const winnerSeats = record.result.winners;
  const potWon = round(record.result.potsAwarded.reduce((s, p) => s + p.amount, 0));

  return {
    streets,
    heroSeat,
    heroPosition: posOf(heroSeat),
    heroCards: record.holeCards,
    finalBoard: record.board,
    stakesLabel: `$${sb}/$${bb}`,
    tableSize: n,
    buttonSeat,
    result: {
      winnerSeats,
      potWon,
      heroWon: winnerSeats.includes(heroSeat),
      showdown: (record.result.showdown?.length ?? 0) > 0,
    },
  };
}

/** Blinds seeded into a street's committed map (only preflop has them); subtracted when carrying the
 *  pot forward so they aren't double-counted (they were already in `potCarried` at hand start). */
function streetStartBlinds(streetIdx: number, sb: number, bb: number): number {
  return streetIdx === 0 ? sb + bb : 0;
}

/** Build ReplaySegments straight from an ENGINE HandState (GH6 counterfactual), NOT from a flat
 *  HandRecord. Why a separate path: `segmentHand` reconstructs the button + street boundaries from a
 *  COMPLETE action list (an imported PokerStars/PHH hand). A generated hand's `history` omits the
 *  pre-decision preflop folds (generate() marks those seats folded without recording the action), so
 *  the flat list is incomplete and the reconstruction mis-fires. Here we don't reconstruct anything -
 *  the state carries the AUTHORITATIVE `buttonSeat` and every history entry is already street-tagged, so
 *  we bucket by `h.street` and label positions off the real button. Same ReplaySegments shape → the same
 *  <ReplayTimeline> renders it. `startStack` is the uniform per-seat starting stack (winners are derived
 *  from stack deltas, the same NET convention as the importer's PHH path). */
export function segmentsFromHandState(
  state: HandState,
  heroCards: readonly [CardIndex, CardIndex],
  startStack: number,
): ReplaySegments {
  const n = state.players.length;
  const heroSeat = state.spec.heroSeat;
  const { sb, bb, ante = 0 } = state.spec.stakes;
  const buttonSeat = state.buttonSeat;
  const positions = assignPositions(n, buttonSeat);
  const posOf = (seat: number): Position => positions.get(seat) ?? 'BTN';
  const sbSeat = [...positions].find(([, p]) => p === 'SB')?.[0];
  const bbSeat = [...positions].find(([, p]) => p === 'BB')?.[0];

  // bucket the already-street-tagged history - no boundary reconstruction needed.
  const byStreet = new Map<Street, Action[]>();
  for (const h of state.history) {
    const list = byStreet.get(h.street) ?? [];
    list.push(h.action);
    byStreet.set(h.street, list);
  }

  const streets: ReplayStreet[] = [];
  let potCarried = round(sb + bb + ante * n);
  STREETS.forEach((st, idx) => {
    const acts = byStreet.get(st);
    if (!acts || acts.length === 0) return; // a street with no recorded action (e.g. all-in run-out) is skipped

    const committed = new Map<number, number>();
    let currentBet = 0;
    if (idx === 0) {
      if (sbSeat !== undefined) committed.set(sbSeat, sb);
      if (bbSeat !== undefined) committed.set(bbSeat, bb);
      currentBet = bb;
    }
    const streetStartPot = potCarried;
    const moves: ReplayMove[] = acts.map((a) => {
      const isHero = a.seat === heroSeat;
      const move: ReplayMove = {
        seat: a.seat,
        who: isHero ? 'Hero' : posOf(a.seat),
        isHero,
        kind: a.kind,
        verb: VERB[a.kind],
        aggressor: false,
      };
      if (a.kind === 'fold') {
        // no chips
      } else if (a.kind === 'check') {
        committed.set(a.seat, currentBet);
      } else if (a.kind === 'call') {
        const already = committed.get(a.seat) ?? 0;
        const to = a.amount ?? currentBet; // advance() normalizes a call's amount to the TO total
        move.amount = round(to - already);
        committed.set(a.seat, to);
      } else {
        const to = a.amount ?? currentBet; // bet | raise | jam - TO amount
        move.amount = round(to);
        move.aggressor = true;
        committed.set(a.seat, to);
        currentBet = Math.max(currentBet, to);
      }
      return move;
    });

    let streetPot = 0;
    for (const v of committed.values()) streetPot += v;
    potCarried = round(potCarried + streetPot - streetStartBlinds(idx, sb, bb));
    streets.push({
      street: st,
      label: STREET_LABEL[st],
      board: state.board.slice(0, BOARD_LENGTH_BY_STREET[st]),
      potAtStart: streetStartPot,
      moves,
    });
  });

  // winners/pots from stack deltas (NET, matching the importer's derived PHH path).
  const nonFolded = state.players.filter((p) => !p.folded);
  const gains = state.players
    .map((p) => ({ seat: p.seat, gain: p.stack - startStack }))
    .filter((g) => g.gain > 0.005);
  const winnerSeats = gains.map((g) => g.seat);
  const potWon = round(gains.reduce((s, g) => s + g.gain, 0));

  return {
    streets,
    heroSeat,
    heroPosition: posOf(heroSeat),
    heroCards,
    finalBoard: state.board,
    stakesLabel: `$${sb}/$${bb}`,
    tableSize: n,
    buttonSeat,
    result: {
      winnerSeats,
      potWon,
      heroWon: winnerSeats.includes(heroSeat),
      showdown: nonFolded.length > 1,
    },
  };
}
